import { Instance, types, flow, cast } from 'mobx-state-tree';
import moment from 'moment';
import axios from 'axios';

import endpoints from '@configs/endpoints';
import { PAYMENT_LINK_STATUS_NOT_ALLOWED_TO_SHOW, PaymentLinkStatus } from '@constants/paymentLink';
import routes from '@constants/routes';
import { PAYMENT_LINK_NOT_FOUND, PaymentLinkErrors } from '@constants/paymentLinkErrors';
import { DATE_FORMAT_V1 } from '@constants/date';
import Common from '@store/common';

import CoBrandingConfig from './models/Cobranding';
import AccountInfo from './models/AccountInfo';
import PaymentConfig from './models/PaymentConfig';
import Flags from './models/Flags';
import Errors from './models/Errors';
import PaymentLink from './models/PaymentLink';
import { PaymentMethod } from '@store/checkout';

interface IErrors {
  code: PaymentLinkErrors;
}

interface IError {
  errorCode: string;
  errors: IErrors;
}

const PaymentLinkStore = types
  .compose(
    Common,
    types.model({
      paymentLink: types.optional(PaymentLink, {}),
      accountInfo: types.optional(AccountInfo, {}),
      flags: types.optional(Flags, {}),
      errors: types.optional(Errors, {}),
      paymentConfig: types.optional(PaymentConfig, {}),
      coBrandingConfig: types.optional(CoBrandingConfig, {}),
    }),
  )
  .actions((self) => ({
    setAsPaid() {
      const date = moment().format(DATE_FORMAT_V1);
      self.paymentLink.lastPaidAt = date;
      if (!self.paymentLink.isSubscription) {
        self.paymentLink.status = PaymentLinkStatus.CLOSED;
      }
      if (
        self.paymentLink.isSubscription &&
        self.paymentLink.status === PaymentLinkStatus.SUBSCRIBED
      ) {
        self.paymentLink.status = PaymentLinkStatus.SUBSCRIBED;
      }
      if (
        self.paymentLink.isSubscription &&
        self.paymentLink.status === PaymentLinkStatus.SUBSCRIPTION_CANCELED
      ) {
        self.paymentLink.status = PaymentLinkStatus.SUBSCRIPTION_CANCELED;
      }
    },
    setIsRequesting(value: boolean) {
      self.flags.isRequesting = value;
    },
  }))
  .actions((self) => ({
    getPaymentLink: flow(function* (uuid: string) {
      if (self.flags.isRequesting) return;

      try {
        self.flags.isRequesting = true;
        self.errors.fetchingLink = false;

        const { data } = yield axios.get(endpoints.paymentLink(uuid));

        if (PAYMENT_LINK_STATUS_NOT_ALLOWED_TO_SHOW.includes(data.paymentLink.status)) {
          self.updateField('errors.fetchingLink', true);
          return window.location.replace(routes.NOT_FOUND);
        }
        self.paymentLink.updatePaymentLink(data.paymentLink);
        self.coBrandingConfig = data.cobranding;
        self.accountInfo = data.accountInfo;
        self.paymentConfig = data.paymentConfig;
        if (data.paymentLink.paymentMethod) {
          self.globalStore.checkout.setPaymentMethod(data.paymentLink.paymentMethod);
        } else {
          self.globalStore.checkout.setPaymentMethod(
            self.globalStore.checkout.defaultPaymentMethod,
          );
        }
      } catch (e) {
        const err = e as IError;
        self.updateField('errors.fetchingLink', true);
        const { errors, errorCode } = err;

        if (errors?.code === PaymentLinkErrors.PAYMENT_LINK_DELETED) {
          window.location.replace(routes.DELETED);
        }
        if ((errors && PAYMENT_LINK_NOT_FOUND.includes(errors.code)) || errorCode === 'NPE') {
          window.location.replace(routes.NOT_FOUND);
        }
      } finally {
        self.flags.isRequesting = false;
      }
    }),
  }));

export type IPaymentLinkStore = Instance<typeof PaymentLinkStore>;
export default PaymentLinkStore;
